import { useMemo, useState } from 'react';
import { ChevronRight, Cpu, FileText, Paperclip, ShieldCheck } from 'lucide-react';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { CollapsibleSection } from '@/components/collapsible-section';
import { StatusPill } from '@/components/status-dot';
import { usePortalAssets } from '@/lib/portal-assets';
import { groupAssetsByCategory } from '@/lib/group-assets';
import { formatDate, formatStatus } from '@/lib/format';
import type { AssetAttachmentData, PortalAssetData } from '@/types/api';

function statusTone(status: string): 'success' | 'warning' | 'neutral' | 'danger' {
  if (status === 'active') return 'success';
  if (status === 'maintenance') return 'warning';
  if (status === 'retired') return 'neutral';
  return 'neutral';
}

/**
 * Read-only IT asset register for the client portal. What appears here is
 * gated server-side by the client's Assets feature flag.
 */
export function PortalAssets() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data: paginated, isLoading } = usePortalAssets({
    q: q || undefined,
    status: status || undefined,
    page,
  });
  const data = paginated?.data;
  const meta = paginated?.meta;
  const groups = useMemo(() => groupAssetsByCategory(data ?? []), [data]);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Cpu}
        title="IT Assets"
        subtitle="Hardware, software, and equipment Mehrutherm tracks for you."
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search assets…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="max-w-[180px]"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="maintenance">Maintenance</option>
          <option value="retired">Retired</option>
        </Select>
      </div>

      {isLoading ? (
        <SkeletonList rows={5} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Cpu}
          title="No assets yet"
          description="When Mehrutherm documents your hardware and equipment, it will show up here."
        />
      ) : (
        <div className="space-y-4">
          <div className="space-y-3">
            {groups.map(([category, assets]) => (
              <CollapsibleSection key={category} title={category} count={assets.length}>
                <DataList>
                  {assets.map((asset) => (
                    <AssetRow key={asset.id} asset={asset} />
                  ))}
                </DataList>
              </CollapsibleSection>
            ))}
          </div>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function AssetRow({ asset }: { asset: PortalAssetData }) {
  const [open, setOpen] = useState(false);
  const sub = [asset.manufacturer, asset.model].filter(Boolean).join(' ');
  const warranty =
    asset.warranty_expires_at != null
      ? `Warranty ${asset.days_until_warranty_expiry != null && asset.days_until_warranty_expiry < 0 ? 'expired' : 'until'} ${formatDate(asset.warranty_expires_at)}`
      : null;
  const attachments = asset.attachments ?? [];
  const hasAttachments = attachments.length > 0;

  const header = (
    <>
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-surface-muted)] text-[var(--color-foreground-muted)]">
        <Cpu className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-medium text-foreground">{asset.name}</span>
          {asset.identifier ? (
            <span className="truncate text-xs text-muted-foreground">{asset.identifier}</span>
          ) : null}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="capitalize">{asset.type}</span>
          {sub ? (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{sub}</span>
            </>
          ) : null}
          {warranty ? (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="size-3" /> {warranty}
              </span>
            </>
          ) : null}
        </div>
      </div>
      {hasAttachments ? (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Paperclip className="size-3.5" />
          {attachments.length}
        </span>
      ) : null}
      <StatusPill tone={statusTone(asset.status)}>{formatStatus(asset.status)}</StatusPill>
      {hasAttachments ? (
        <ChevronRight
          className={`size-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
        />
      ) : null}
    </>
  );

  return (
    <div>
      {hasAttachments ? (
        <DataListRow interactive onClick={() => setOpen((v) => !v)}>
          {header}
        </DataListRow>
      ) : (
        <DataListRow interactive={false}>{header}</DataListRow>
      )}
      {hasAttachments && open ? <AssetAttachments attachments={attachments} /> : null}
    </div>
  );
}

function AssetAttachments({ attachments }: { attachments: AssetAttachmentData[] }) {
  const photos = attachments.filter((a) => a.is_image);
  const docs = attachments.filter((a) => !a.is_image);

  return (
    <div className="border-t border-[var(--color-border)] bg-[var(--color-surface-muted)]/40 px-4 py-3 pl-16">
      {photos.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {photos.map((photo) => (
            <a
              key={photo.id}
              href={photo.url}
              target="_blank"
              rel="noreferrer"
              className="group relative block size-20 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]"
              title={photo.file_name}
            >
              <img
                src={photo.url}
                alt={photo.file_name}
                className="size-full object-cover transition-transform group-hover:scale-105"
              />
            </a>
          ))}
        </div>
      ) : null}
      {docs.length > 0 ? (
        <div className={`flex flex-wrap gap-2 ${photos.length > 0 ? 'mt-2.5' : ''}`}>
          {docs.map((doc) => (
            <a
              key={doc.id}
              href={doc.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
              title={doc.file_name}
            >
              <FileText className="size-3.5 shrink-0" />
              <span className="max-w-[16rem] truncate">{doc.file_name}</span>
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}
